import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePortal } from '../PortalContext';
import { UDA_THEMES } from '../udaThemes';
import { useAuth } from '../../../context/AuthContext';

const AP_EMBLEM = 'https://cdma.ap.gov.in/assets/logos/ap_state_logo_new.png';

interface Slide {
  id: number;
  image: string;
  title: string;
  subtitle: string;
  badge: string;
  cta: string;
  href: string;
}

const fallbackSlides: Slide[] = [
  { id: 1, image: 'https://picsum.photos/seed/dtcp-carousel-1/1400/600', title: 'Building a Better Tomorrow', subtitle: 'Streamlined building permissions and urban planning services for Andhra Pradesh', badge: 'Building Permissions', cta: 'Apply Now', href: '#services' },
  { id: 2, image: 'https://picsum.photos/seed/dtcp-carousel-2/1400/600', title: 'Master Plans for Urban Growth', subtitle: 'Comprehensive master plans guiding sustainable urban development across all UDAs', badge: 'Master Plans', cta: 'View Plans', href: '#services' },
  { id: 3, image: 'https://picsum.photos/seed/dtcp-carousel-3/1400/600', title: 'Digital Services at Your Fingertips', subtitle: 'Apply online for permits, track applications, and download certificates 24/7', badge: 'Online Services', cta: 'Get Started', href: '#services' },
  { id: 4, image: 'https://picsum.photos/seed/dtcp-carousel-4/1400/600', title: 'Layout Approvals Made Simple', subtitle: 'Fast and transparent layout approval process for developers and citizens', badge: 'Layout Approvals', cta: 'Learn More', href: '#services' },
  { id: 5, image: 'https://picsum.photos/seed/dtcp-carousel-5/1400/600', title: 'Planned Growth, Prosperous Future', subtitle: 'Regulating urban development and ensuring planned growth since 1920', badge: 'About DTCP', cta: 'Know More', href: '#about' },
];

// A seed script once wrote a JSON metadata blob into dscn_tx, which rendered raw as
// the slide subtitle. Never show a serialized object to a citizen — blank it instead.
const plainText = (s: string) => (/^\s*[{[]/.test(s) ? '' : s);

export const PortalHeroSection: React.FC = () => {
  const { t } = useTranslation();
  const { siteId, siteName, publicApi } = usePortal();
  const { isAuthenticated, user } = useAuth();
  // Badge shows the UDA the portal is currently scoped to (e.g. "Amalapuram",
  // "Rajahmundry"). Prefer the registry short name resolved by site id; fall back
  // to the context site name (DTCP main site has no UDA_THEMES entry).
  const udaBadge = Object.values(UDA_THEMES).find((th) => th.id === siteId)?.shortName || siteName;
  const [slides, setSlides] = useState<Slide[]>(fallbackSlides);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    setCurrent(0);
    publicApi.get(`/gallery/images/homepage_carousel?siteId=${siteId}`)
      .then(res => {
        const data = res.data?.data || [];
        if (data.length > 0) {
          setSlides(data.map((img: any, i: number) => {
            return {
              id: img.imge_id || i + 1,
              image: img.imge_url_tx || '',
              title: img.imge_nm || '',
              // Description comes straight from prvdr_glry_imgs_lst_t.dscn_tx —
              // the text the admin types in Manage Gallery / Carousel. Badge and CTA
              // are not stored per-image, so they keep the i18n defaults below.
              subtitle: plainText(img.dscn_tx || ''),
              badge: '', cta: 'Learn More',
              href: '#services',
            };
          }));
        } else {
          setSlides(fallbackSlides);
        }
      })
      .catch(() => setSlides(fallbackSlides));
  }, [siteId]);

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 700);
    },
    [animating]
  );

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  // Auto-advance. Deliberately NOT driven by `next`/`current`: a callback that closes
  // over `current` is rebuilt on every slide change (and on each `animating` flip),
  // which would tear down and re-arm this interval constantly and stall the rotation.
  // Using a functional state update keeps `advance` stable, so the timer fires cleanly
  // every 6s. It still toggles `animating` so the content fade plays like a manual click.
  const advance = useCallback(() => {
    setAnimating(true);
    setCurrent((c) => (c + 1) % slides.length);
    setTimeout(() => setAnimating(false), 700);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(advance, 6000);
    return () => clearInterval(timer);
  }, [advance, slides.length]);

  const slide = slides[current];

  // Slide 1 carries the UDA branding. State/DTCP users have no udaName, so they
  // keep the stock first slide.
  const showUdaBranding = current === 0 && isAuthenticated && !!user?.udaName;

  return (
    <section
      id="home"
      className="relative w-full overflow-hidden bg-[#12161F]"
      style={{ height: 'clamp(360px, 64vh, 620px)' }}
    >
      {/* Background Images with crossfade + zoom */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{
            opacity: i === current ? 1 : 0,
            backgroundImage: `url('${s.image}')`,
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            transition: 'opacity 700ms ease',
          }}
        />
      ))}

      {/* No dark scrim over the photo — carousel images (often branded banners)
          must show at full clarity. Text legibility comes from the drop-shadows
          on the title/subtitle instead. Only a thin bottom fade remains so the
          slide dots/counter stay visible. */}
      <div
        className="absolute inset-x-0 bottom-0 h-20 z-10 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, rgba(9,12,18,0.35) 0%, transparent 100%)',
        }}
      />

      {/* Content — the first slide is the UDA's identity card (emblem + code + full
          name); the rest keep the normal badge/title/subtitle/CTA layout. */}
      {showUdaBranding ? (
        <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-6">
          <img
            src={AP_EMBLEM}
            alt="Government of Andhra Pradesh Emblem"
            className={`w-16 h-16 md:w-24 md:h-24 object-contain mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-all duration-500 ${
              animating ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'
            }`}
          />
          <h2
            className={`font-extrabold text-white leading-none tracking-[0.06em] text-[52px] md:text-[84px] lg:text-[104px] drop-shadow-[0_3px_12px_rgba(0,0,0,0.5)] transition-all duration-500 ${
              animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
            style={{ transitionDelay: '80ms' }}
          >
            {user?.udaCode || user?.udaName}
          </h2>
          <p
            className={`text-white/90 font-medium uppercase tracking-[0.15em] mt-4 text-[13px] md:text-[20px] lg:text-[26px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-all duration-500 ${
              animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
            style={{ transitionDelay: '160ms' }}
          >
            {user?.udaName}
          </p>
        </div>
      ) : (
      <div className="relative z-20 h-full flex items-center">
        <div className="w-full px-5 sm:pl-10 lg:pl-20 sm:pr-16">
          <div className="max-w-full sm:max-w-[640px]">
            {/* Eyebrow — a glass pill instead of the hard amber box, which read
                as a warning label sitting on the photograph. */}
            <div
              className={`flex items-center gap-3 mb-6 transition-all duration-500 ${
                animating ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'
              }`}
            >
              <span className="w-9 h-[3px] rounded-full bg-[var(--portal-accent)]" />
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-md px-3.5 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.16em] text-white">
                <span
                  className="w-1.5 h-1.5 rounded-full bg-[var(--portal-accent-light)]"
                  style={{ boxShadow: '0 0 0 3px rgba(255,138,61,0.25)' }}
                />
                {udaBadge}
              </span>
            </div>

            {/* Title */}
            <h2
              className={`font-extrabold text-white leading-[1.06] tracking-[-0.02em] mb-0 transition-all duration-500 text-[30px] md:text-[40px] lg:text-[52px] drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)] ${
                animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
              }`}
              style={{ transitionDelay: '80ms' }}
            >
              {slide.title || t(`hero.slide${current + 1}.title`)}
            </h2>

            {/* Subtitle */}
            <p
              className={`text-white leading-relaxed mt-5 max-w-[540px] transition-all duration-500 text-[15px] lg:text-[17px] drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)] ${
                animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
              }`}
              style={{ transitionDelay: '160ms' }}
            >
              {slide.subtitle}
            </p>

            {/* CTA */}
            <div
              className={`mt-8 flex flex-wrap items-center gap-3 transition-all duration-500 ${
                animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
              }`}
              style={{ transitionDelay: '240ms' }}
            >
              <a
                href={slide.href}
                className="
                  group relative inline-flex items-center gap-2 overflow-hidden
                  bg-gradient-to-b from-[var(--portal-accent)] to-[var(--portal-accent-dark)]
                  text-white px-7 py-3.5 font-semibold text-sm rounded-full
                  transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap
                  shadow-[0_10px_28px_-8px_rgba(230,106,26,0.85)]
                  hover:shadow-[0_16px_36px_-10px_rgba(230,106,26,0.95)]
                "
              >
                {/* Sheen sweep on hover */}
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                <span className="relative">{slide.cta || t(`hero.slide${current + 1}.cta`)}</span>
                <i className="relative ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
              </a>

              <a
                href="#services"
                className="
                  inline-flex items-center gap-2 rounded-full px-6 py-3.5
                  bg-white/10 ring-1 ring-white/25 backdrop-blur-md
                  text-white font-semibold text-sm whitespace-nowrap
                  transition-all duration-300 hover:bg-white/20 hover:-translate-y-0.5 cursor-pointer
                "
              >
                <i className="ri-apps-2-line text-base" />
                All Services
              </a>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Arrow — Previous */}
      <button
        onClick={prev}
        className="
          hidden sm:flex
          absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-20
          w-12 h-12 rounded-full items-center justify-center
          bg-white/10 ring-1 ring-white/25 backdrop-blur-md text-white
          hover:bg-white/25 hover:scale-105 active:scale-95
          transition-all duration-200 cursor-pointer
          focus:outline-none focus-visible:ring-2 focus-visible:ring-white
        "
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>

      {/* Arrow — Next */}
      <button
        onClick={next}
        className="
          hidden sm:flex
          absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-20
          w-12 h-12 rounded-full items-center justify-center
          bg-white/10 ring-1 ring-white/25 backdrop-blur-md text-white
          hover:bg-white/25 hover:scale-105 active:scale-95
          transition-all duration-200 cursor-pointer
          focus:outline-none focus-visible:ring-2 focus-visible:ring-white
        "
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Pill-style dot indicators */}
      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === current}
            className={`h-[5px] rounded-full cursor-pointer transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
              i === current
                ? 'w-8 bg-white shadow-[0_0_12px_rgba(255,255,255,0.55)]'
                : 'w-2.5 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      {/* Slide counter — the current index carries the weight, the total sits
          back, so it reads as a position rather than a pair of numbers. */}
      <div className="hidden sm:flex absolute bottom-6 right-7 z-20 items-baseline tabular-nums tracking-wide">
        <span className="text-white text-lg font-bold leading-none">
          {String(current + 1).padStart(2, '0')}
        </span>
        <span className="text-white/30 mx-1.5 text-xs">/</span>
        <span className="text-white/45 text-xs font-semibold">
          {String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 h-[3px] bg-white/10">
        <div
          key={current}
          className="h-full hero-progress"
          style={{ background: 'linear-gradient(90deg, var(--portal-accent), var(--portal-accent-light))' }}
        />
      </div>
    </section>
  );
};
